import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useI18n } from "@/i18n";

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h3 className="font-heading text-sm font-semibold text-foreground">{title}</h3>
      <div className="space-y-1 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

function PrivacyPolicyContent() {
  const { language } = useI18n();

  if (language === "fi") {
    return (
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground">Päivitetty: 6.6.2026</p>

        <Section title="Rekisterinpitäjä">
          <p>CV-arvioija (cvarvio.fi)</p>
          <p>
            Tietosuoja-asiat:{" "}
            <a href="mailto:tietosuoja@cvarvio.fi" className="underline hover:text-foreground">
              tietosuoja@cvarvio.fi
            </a>
          </p>
        </Section>

        <Section title="Miten palvelu toimii">
          <p>CV-arvioija tarjoaa useita toimintoja:</p>
          <ol className="ml-4 list-decimal space-y-2">
            <li>
              <strong>CV-analyysi —</strong> lataat tai liität CV:si, jonka jälkeen palvelu
              lähettää sen Anthropic Claude -tekoälymallille. Malli tuottaa palautteen viidestä
              osa-alueesta (muotoilu, sisältö, kieli, paikalliset normit, erottautuminen) sekä
              konkreettiset parannusehdotukset. Raportin voi ladata HTML- tai JSON-tiedostona.
            </li>
            <li>
              <strong>CV-optimoija —</strong> lataat CV:si ja työpaikkailmoituksen, jonka jälkeen
              palvelu luo ATS-optimoidun version CV:stäsi, räätälöitynä kyseiseen työpaikkaan.
              Sisältää avainsana-analyysin ja ATS-pisteet ennen/jälkeen.
            </li>
            <li>
              <strong>Tekoälyhaastattelun harjoittelu —</strong> syötät CV-tiivistelmän ja
              taustatiedot, jonka jälkeen Anthropic Claude esittää 6–8 haastattelukysymystä.
              Vastauksesi kulkevat Anthropicille arviointia varten. Haastattelijan ääni
              tuotetaan OpenAI TTS -palvelulla. Haastattelu ja palaute ovat vain istunnon ajan;
              niitä ei tallenneta palvelimelle.
            </li>
            <li>
              <strong>Jaettavat linkit —</strong> voit luoda jaettavan linkin CV-arviointiisi,
              jonka voit lähettää mentorille tai ystävälle palautetta varten. Linkit vanhenevat
              7 päivän kuluttua (maksimi 30 päivää) ja voit asettaa salasanan tai
              katselukertojen rajan.
            </li>
            <li>
              <strong>Edistymisen seuranta —</strong> palvelu voi tallentaa CV-arviointiesi
              pisteet istuntokohtaiseen historiaan, jotta näet edistymisesi ajan myötä. Tämä on
              valinnaista ja perustuu selaimeen tallennettuun istuntotunnisteeseen.
            </li>
          </ol>
          <p className="mt-1">
            Palvelu ei tee profilointia, kohdennettua mainontaa eikä automaattisia päätöksiä,
            jotka vaikuttaisivat oikeudellisesti sinuun.
          </p>
        </Section>

        <Section title="Mitä tietoja käsitellään">
          <p>Palvelu käsittelee vain sen, mitä itse lähetät:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>CV-teksti tai tiedosto (PDF tai DOCX)</li>
            <li>
              Vapaaehtoinen lisätieto: haettava tehtävä, toimiala, kokemustaso, kohdealue,
              työnkuvaus ja erityiset toiveet
            </li>
            <li>
              Haastatteluharjoittelussa: CV-tiivistelmä, haastatteluvastaukset sekä mahdolliset
              äänitallenteet (käsitellään paikallisesti tai lähetetään Emergent AI -yhdyskäytävän
              kautta OpenAI Whisper -transkriptioon)
            </li>
          </ul>
          <p className="mt-1">
            Palvelu ei kerää nimeä, sähköpostiosoitetta, puhelinnumeroa eikä muita
            yhteystietoja — ellei niitä ole CV:n tekstissä.
          </p>
        </Section>

        <Section title="Mihin tietoja käytetään">
          <p>
            Tietoja käytetään ainoastaan CV:n arvioimiseen ja haastattelupalauttteen
            tuottamiseen. Oikeusperuste on GDPR 6 artiklan 1 b kohta — palvelun suorittaminen
            käyttäjän pyynnöstä.
          </p>
        </Section>

        <Section title="Säilytys">
          <p>
            <strong>CV-teksti ja haastatteluvastaukset:</strong> Ei tallenneta palvelimelle.
            Kaikki käsittely tapahtuu muistissa pyynnön ajan, jonka jälkeen tiedot häviävät.
          </p>
          <p className="mt-2">
            <strong>Jaettavat linkit:</strong> Jos luot jaettavan linkin, arvioinnin metadata
            (pisteet, dimensiot, suositukset - EI CV-teksti) tallennetaan palvelimelle
            määrittämäksesi ajaksi (7-30 päivää). Linkin luoneen käyttäjän IP-osoite hashataan
            (SHA-256) väärinkäytön estämiseksi. Linkit poistetaan automaattisesti vanhenemisen
            jälkeen.
          </p>
          <p className="mt-2">
            <strong>Edistymisen seuranta:</strong> Jos käytät edistymisen seurantaa, palvelu
            tallentaa CV-arviointiesi pisteet (EI CV-tekstiä) istuntokohtaiseen tiedostoon
            palvelimelle. Istuntotunnus hashataan (SHA-256). Historiatiedot poistetaan
            automaattisesti 90 päivän kuluttua. Voit poistaa historiasi milloin tahansa.
          </p>
          <p className="mt-2">
            <strong>Välimuisti:</strong> Palvelu voi välimuistissa palvelimella CV-arvioinnin
            tulokset (EI CV-tekstiä) 1-2 tunnin ajaksi suorituskyvyn parantamiseksi, jos lähetät
            saman CV:n useasti. Välimuisti tyhjenee automaattisesti.
          </p>
        </Section>

        <Section title="Kolmannet osapuolet">
          <p>CV-teksti ja haastatteluvastaukset lähetetään seuraaville palveluille:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Emergent AI</strong> — LLM-yhdyskäytävä, jonka kautta pyyntö välitetään
              Anthropicille ja OpenAI:lle. CV-teksti ja haastatteluvastaukset kulkevat tämän
              palvelun kautta.{" "}
              <a
                href="https://emergent.sh/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                emergent.sh/privacy
              </a>
            </li>
            <li>
              <strong>Anthropic Claude</strong> — tuottaa CV-arviointiraportin ja
              haastattelukysymykset (Emergent AI -yhdyskäytävän kautta).{" "}
              <a
                href="https://www.anthropic.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                anthropic.com/privacy
              </a>
            </li>
            <li>
              <strong>OpenAI TTS</strong> — haastatteluvalmentajan äänisynteesi (Emergent AI
              -yhdyskäytävän kautta). Tekstiä lähetetään äänentoisintoa varten, ei tallenneta.{" "}
              <a
                href="https://openai.com/policies/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                openai.com/policies/privacy-policy
              </a>
            </li>
            <li>
              <strong>Vercel</strong> — palvelun hostaus, palvelimet pääosin Yhdysvalloissa.{" "}
              <a
                href="https://vercel.com/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                vercel.com/legal/privacy-policy
              </a>
            </li>
            <li>
              <strong>Cloudflare Turnstile</strong> — bottienesto lomakkeessa. Ei
              seurantaevästeitä, CV:n sisältöä ei lähetetä Cloudflarelle.{" "}
              <a
                href="https://www.cloudflare.com/privacypolicy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                cloudflare.com/privacypolicy
              </a>
            </li>
          </ul>
          <p className="mt-1">Tietoja ei myydä eikä luovuteta markkinointitarkoituksiin.</p>
        </Section>

        <Section title="Tietoturva">
          <p>
            Kaikki liikenne kulkee salatun HTTPS-yhteyden kautta. Koska mitään ei tallenneta
            palvelimelle, tietomurron riski on pieni. Palvelu käyttää pyyntökohtaisia
            kokorajoituksia ja nopeusrajoituksia väärinkäytön estämiseksi.
          </p>
        </Section>

        <Section title="Evästeet ja paikallinen tallennus">
          <p>
            <strong>sessionStorage:</strong> Palvelu käyttää selaimen sessionStoragea CV-tietojen
            väliaikaiseen siirtämiseen sivujen välillä. Tiedot poistuvat automaattisesti, kun
            suljet välilehden.
          </p>
          <p className="mt-2">
            <strong>localStorage:</strong> Jos käytät edistymisen seurantaa, istuntotunniste
            tallennetaan selaimen localStorageen, jotta historiatietosi säilyvät. Voit poistaa
            tämän tunnisteen milloin tahansa selaimesi asetuksista.
          </p>
          <p className="mt-2">
            Palvelussa ei ole seurantaevästeitä eikä analytiikkaa.
          </p>
        </Section>

        <Section title="Oikeutesi">
          <p>Sinulla on oikeus:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>saada tietoa käsittelystä (tämä seloste)</li>
            <li>
              pyytää tietojesi poistamista — koska tietoja ei tallenneta, pyyntö täyttyy
              automaattisesti istunnon päätyttyä
            </li>
            <li>
              tehdä valitus Tietosuojavaltuutetun toimistolle:{" "}
              <strong>tietosuoja.fi</strong>
            </li>
          </ul>
          <p className="mt-1">
            Kysy lisää:{" "}
            <a href="mailto:tietosuoja@cvarvio.fi" className="underline hover:text-foreground">
              tietosuoja@cvarvio.fi
            </a>
          </p>
        </Section>

        <Section title="Muutokset">
          <p>Selosteen muutoksista ilmoitetaan päivittämällä yläreunan päivämäärä.</p>
        </Section>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">Last updated: 3 May 2026</p>

      <Section title="Controller">
        <p>CV Reviewer (cvarvio.fi)</p>
        <p>
          Privacy enquiries:{" "}
          <a href="mailto:tietosuoja@cvarvio.fi" className="underline hover:text-foreground">
            tietosuoja@cvarvio.fi
          </a>
        </p>
      </Section>

      <Section title="How the service works">
        <p>CV Reviewer provides two features:</p>
        <ol className="ml-4 list-decimal space-y-2">
          <li>
            <strong>CV analysis —</strong> you upload or paste your CV, which is sent to the
            Anthropic Claude AI model. The model returns feedback across five dimensions
            (formatting, content, language, local norms, strategic positioning) along with
            concrete rewrite suggestions. You can download the report as an HTML or JSON file.
          </li>
          <li>
            <strong>Mock interview practice —</strong> you provide a CV summary and context,
            after which Anthropic Claude asks 6–8 tailored interview questions. Your answers
            are sent to Anthropic for evaluation. The interviewer's voice is produced by the
            OpenAI TTS service. The interview transcript and feedback exist only for the
            duration of your session and are never stored on the server.
          </li>
        </ol>
        <p className="mt-1">
          The service does not perform profiling, targeted advertising, or automated decisions
          that have a legal or similarly significant effect on you.
        </p>
      </Section>

      <Section title="What data is processed">
        <p>The service only processes what you submit:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Your CV text or file (PDF or DOCX)</li>
          <li>
            Optional context: job title, industry, seniority, target market, job description,
            and specific concerns
          </li>
          <li>
            In mock interview mode: CV summary, interview answers, and optional audio recordings
            (processed locally or sent via the Emergent AI gateway to OpenAI Whisper for
            transcription)
          </li>
        </ul>
        <p className="mt-1">
          The service does not collect your name, email, phone number, or any other contact
          details — unless they appear in your CV text.
        </p>
      </Section>

      <Section title="Why data is used">
        <p>
          Data is used solely to review your CV and generate interview feedback. Legal basis:
          GDPR Art. 6(1)(b) — performing the service you requested.
        </p>
      </Section>

      <Section title="Retention">
        <p>
          <strong>CV text and interview answers:</strong> Not stored on the server. Everything
          is processed in memory for the duration of the request and then discarded.
        </p>
        <p className="mt-2">
          <strong>Shareable links:</strong> If you create a shareable link, the review metadata
          (scores, dimensions, recommendations - NOT CV text) is stored on the server for your
          chosen duration (7-30 days). The IP address of the link creator is hashed (SHA-256) to
          prevent abuse. Links are automatically deleted after expiration.
        </p>
        <p className="mt-2">
          <strong>Progress tracking:</strong> If you use progress tracking, the service stores
          your CV review scores (NOT CV text) in a session-specific file on the server. The
          session identifier is hashed (SHA-256). History data is automatically deleted after 90
          days. You can delete your history at any time.
        </p>
        <p className="mt-2">
          <strong>Cache:</strong> The service may cache CV review results (NOT CV text) on the
          server for 1-2 hours to improve performance if you submit the same CV multiple times.
          The cache is automatically cleared.
        </p>
      </Section>

      <Section title="Third parties">
        <p>Your CV text and interview answers are sent to the following services:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <strong>Emergent AI</strong> — LLM gateway through which requests are forwarded to
            Anthropic and OpenAI. Your CV text and interview answers transit this service.{" "}
            <a
              href="https://emergent.sh/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              emergent.sh/privacy
            </a>
          </li>
          <li>
            <strong>Anthropic Claude</strong> — generates the CV review report and interview
            questions (via the Emergent AI gateway).{" "}
            <a
              href="https://www.anthropic.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              anthropic.com/privacy
            </a>
          </li>
          <li>
            <strong>OpenAI TTS</strong> — voice synthesis for the interview coach (via the
            Emergent AI gateway). Text is sent for audio generation only and is not stored.{" "}
            <a
              href="https://openai.com/policies/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              openai.com/policies/privacy-policy
            </a>
          </li>
          <li>
            <strong>Vercel</strong> — hosting, servers primarily in the United States.{" "}
            <a
              href="https://vercel.com/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              vercel.com/legal/privacy-policy
            </a>
          </li>
          <li>
            <strong>Cloudflare Turnstile</strong> — bot protection on the form. No tracking
            cookies; your CV content is never sent to Cloudflare.{" "}
            <a
              href="https://www.cloudflare.com/privacypolicy/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              cloudflare.com/privacypolicy
            </a>
          </li>
        </ul>
        <p className="mt-1">Data is never sold or shared for marketing purposes.</p>
      </Section>

      <Section title="Security">
        <p>
          All traffic is encrypted via HTTPS. Because nothing is stored on the server, the
          risk from a breach is minimal. The service applies request size limits and rate
          limiting to prevent abuse.
        </p>
      </Section>

      <Section title="Cookies and local storage">
        <p>
          <strong>sessionStorage:</strong> The service uses browser sessionStorage to temporarily
          carry CV data between pages. This data is automatically cleared when the tab is closed.
        </p>
        <p className="mt-2">
          <strong>localStorage:</strong> If you use progress tracking, the session identifier is
          stored in your browser's localStorage so your history persists. You can delete this
          identifier at any time from your browser settings.
        </p>
        <p className="mt-2">
          No tracking cookies or analytics are used.
        </p>
      </Section>

      <Section title="Your rights">
        <p>You have the right to:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <strong>Be informed:</strong> This notice explains what data is processed
          </li>
          <li>
            <strong>Access:</strong> You can export your progress history in JSON format via the
            service (if using tracking)
          </li>
          <li>
            <strong>Deletion:</strong> CV text and interview answers are automatically deleted
            when your session ends. Shareable links and progress history can be deleted at any
            time via the service or by requesting deletion at tietosuoja@cvarvio.fi
          </li>
          <li>
            <strong>Withdraw consent:</strong> You can stop using progress tracking and shareable
            links at any time. Delete the session identifier from your browser and history data
            will be automatically deleted after 90 days
          </li>
          <li>
            <strong>Complain:</strong> Lodge a complaint with a supervisory authority (in Finland:{" "}
            <strong>tietosuoja.fi</strong>)
          </li>
        </ul>
        <p className="mt-2">
          Questions or deletion requests:{" "}
          <a href="mailto:tietosuoja@cvarvio.fi" className="underline hover:text-foreground">
            tietosuoja@cvarvio.fi
          </a>
        </p>
      </Section>

      <Section title="Changes">
        <p>
          Any changes will be communicated by updating the date at the top of this notice.
        </p>
      </Section>
    </div>
  );
}

export function PrivacyPolicyDialog({ trigger }) {
  const { language } = useI18n();
  const title = language === "fi" ? "Tietosuojaseloste" : "Privacy Policy";

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4" data-testid="privacy-policy-scroll-area">
          <PrivacyPolicyContent />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
